import { taskAxiosInstance } from "../axiosInstance";
import type { AttributedReaction, ReactionType } from "frontend-components";

export type AnnouncementIcon =
    | "ANNOUNCEMENT"
    | "CALENDAR"
    | "PEOPLE"
    | "ALERT"
    | "BELL"
    | "SALARY"
    | "GLOBAL"
    | "INFO";

export interface AnnouncementCreatedBy {
    id: number;
    firstName: string;
    lastName: string;
}

export interface Announcement {
    id: number;
    title: string;
    content: string | null;
    icon: AnnouncementIcon;
    instanceId: number;
    createdAt: string;
    updatedAt: string;
    createdById: number;
    createdBy: AnnouncementCreatedBy;
    reactionCounts: {
        type: ReactionType;
        count: number;
    }[];
    currentUserReaction: ReactionType | null;
}

export interface AnnouncementQuery {
    instanceId: number;
    skip?: number;
    take?: number;
}

export interface AnnouncementListResponse {
    data: Announcement[];
    total: number;
}

export interface CreateAnnouncementInput {
    instanceId: number;
    title: string;
    content?: string | null;
    icon?: AnnouncementIcon;
}

export interface UpdateAnnouncementInput {
    title?: string;
    content?: string | null;
    icon?: AnnouncementIcon;
}

export const getAnnouncements = async (
    query: AnnouncementQuery,
): Promise<AnnouncementListResponse> => {
    const params = new URLSearchParams();
    params.set("instanceId", query.instanceId.toString());
    if (query.skip !== undefined) params.set("skip", query.skip.toString());
    if (query.take !== undefined) params.set("take", query.take.toString());

    const queryString = params.toString();
    const response = await taskAxiosInstance.get<AnnouncementListResponse>(
        `/announcements${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
};

export const createAnnouncement = async (data: CreateAnnouncementInput): Promise<Announcement> => {
    const response = await taskAxiosInstance.post<Announcement>("/announcements", data);
    return response.data;
};

export const updateAnnouncement = async (
    id: number,
    data: UpdateAnnouncementInput,
): Promise<Announcement> => {
    const response = await taskAxiosInstance.patch<Announcement>(`/announcements/${id}`, data);
    return response.data;
};

export const deleteAnnouncement = async (id: number): Promise<Announcement> => {
    const response = await taskAxiosInstance.post<Announcement>(`/announcements/${id}/delete`);
    return response.data;
};

export const setAnnouncementReaction = async (
    id: number,
    type: ReactionType,
): Promise<Announcement> => {
    const response = await taskAxiosInstance.put<Announcement>(`/announcements/${id}/reaction`, {
        type,
    });
    return response.data;
};

export const deleteAnnouncementReaction = async (id: number): Promise<Announcement> => {
    const response = await taskAxiosInstance.delete<Announcement>(`/announcements/${id}/reaction`);
    return response.data;
};

export const getAnnouncementReactions = async (id: number): Promise<AttributedReaction[]> => {
    const response = await taskAxiosInstance.get<AttributedReaction[]>(
        `/announcements/${id}/reactions`,
    );
    return response.data;
};
